import React from 'react';
import { Link } from 'react-router-dom';
import Logo from '../components/Logo';

const Careers = () => {
	return (
		<div className="min-h-screen bg-gray-50">
			<header className="bg-white shadow-sm border-b border-gray-200">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
					<div className="flex items-center justify-between">
						<Link to="/">
							<Logo showText={true} width={50} height={50} />
						</Link>
						<Link
							to="/"
							className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
						>
							Back to Home
						</Link>
					</div>
				</div>
			</header>

			<div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
				<div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
					<h1 className="text-4xl font-bold text-gray-900 mb-4">Careers</h1>
					<p className="text-gray-600 mb-10 text-lg">
						Join the team building the future of startup–investor collaboration. We’re a small team with a big mission: make fundraising and due diligence secure, transparent, and simple for everyone.
					</p>

					<div className="prose prose-lg max-w-none">
						<section className="mb-10">
							<h2 className="text-2xl font-semibold text-gray-900 mb-4">Why Join iVault</h2>
							<p className="text-gray-700 mb-4">
								We’re building a product that founders and investors use at one of the most important moments in a company’s life—the raise. You’ll work on security, product, and UX that directly impact how deals get done. We care about clarity, reliability, and keeping our users’ data safe. If you like solving hard problems and shipping features that people rely on every day, you’ll fit right in.
							</p>
						</section>

						<section className="mb-10">
							<h2 className="text-2xl font-semibold text-gray-900 mb-4">What We Look For</h2>
							<ul className="list-disc pl-6 text-gray-700 space-y-2">
								<li><strong>Ownership</strong> — You take responsibility for your work from idea to release and beyond.</li>
								<li><strong>Security mindset</strong> — You think about edge cases, access control, and data protection by default.</li>
								<li><strong>User focus</strong> — You care about how founders and investors actually use the product, not just the tech.</li>
								<li><strong>Clear communication</strong> — You write and speak clearly and work well in a distributed, async-friendly environment.</li>
							</ul>
						</section>

						<section className="mb-10">
							<h2 className="text-2xl font-semibold text-gray-900 mb-4">Benefits & Culture</h2>
							<p className="text-gray-700 mb-4">
								We offer a supportive environment where you can do your best work. Benefits include:
							</p>
							<ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
								<li>Competitive salary and equity where applicable</li>
								<li>Flexible working hours and remote-friendly culture</li>
								<li>Learning budget for courses, books, and conferences</li>
								<li>Health and wellness support</li>
								<li>Transparent communication and direct access to leadership</li>
							</ul>
							<p className="text-gray-700">
								We value work–life balance and sustainable pace. We’d rather ship consistently than burn out.
							</p>
						</section>

						<section className="mb-10">
							<h2 className="text-2xl font-semibold text-gray-900 mb-4">Open Roles</h2>
							<p className="text-gray-700 mb-4">
								We don’t always have open positions, but when we do, we list them here. Right now we’re especially interested in:
							</p>
							<ul className="list-none space-y-4">
								<li className="p-4 bg-gray-50 rounded-lg border border-gray-100">
									<span className="font-semibold text-gray-900 block">Software Engineer (Full Stack)</span>
									<span className="text-sm text-gray-600">Build and maintain secure, scalable features for data rooms, permissions, and integrations. Strong experience in React and Java/Spring preferred.</span>
								</li>
								<li className="p-4 bg-gray-50 rounded-lg border border-gray-100">
									<span className="font-semibold text-gray-900 block">Product Designer</span>
									<span className="text-sm text-gray-600">Own UX for core flows: data room setup, sharing, and investor experience. You’ll work closely with engineering and customers.</span>
								</li>
								<li className="p-4 bg-gray-50 rounded-lg border border-gray-100">
									<span className="font-semibold text-gray-900 block">Customer Success / Support</span>
									<span className="text-sm text-gray-600">Help startups and investors get the most out of iVault. You’ll be the voice of the user and help shape the product.</span>
								</li>
							</ul>
							<p className="text-gray-700 mt-4 text-sm">
								Don’t see a perfect fit? We’re always open to hearing from talented people. Send us your background and what you’re interested in.
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-2xl font-semibold text-gray-900 mb-4">How to Apply</h2>
							<p className="text-gray-700 mb-4">
								Send us an email with your resume and a short note on why you’re interested in iVault and what you’d bring to the team. We read every application and aim to respond within a week.
							</p>
							<p className="text-gray-700">
								<Link to="/contact" className="text-blue-600 hover:underline font-medium">Contact us</Link> with the subject line “Careers – [Role]” and we’ll take it from there.
							</p>
						</section>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Careers;
